import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'reactstrap';
import './ActionsMenu.css';

export interface ActionsMenuItem {
    label: string;
    onClick: () => void;
    icon?: string;
    danger?: boolean;
    disabled?: boolean;
    // Insere uma linha divisória ACIMA deste item — usado para separar ações de risco
    // (excluir) ou ações em lote das ações individuais.
    dividerBefore?: boolean;
}

interface ActionsMenuProps {
    items: ActionsMenuItem[];
    title?: string;
}

const MENU_WIDTH = 240;
const ITEM_HEIGHT = 36;
const MENU_PADDING = 8;
const VIEWPORT_MARGIN = 8;

// Menu de ações (ícone de 3 pontinhos) renderizado via portal em document.body, com posição
// calculada a partir do botão que o abriu — assim nunca fica cortado pelo overflow da tabela
// (table-responsive, modo tela cheia) nem sai da viewport: abre pra baixo quando há espaço,
// pra cima quando não há.
export const ActionsMenu = ({ items, title }: ActionsMenuProps) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState<React.CSSProperties>({});
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const posicionar = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const alturaEstimada = items.length * ITEM_HEIGHT + MENU_PADDING * 2;

        const espacoAbaixo = window.innerHeight - rect.bottom;
        const espacoAcima = rect.top;
        const abrirParaCima = espacoAbaixo < alturaEstimada && espacoAcima > espacoAbaixo;

        const left = Math.min(
            Math.max(rect.right - MENU_WIDTH, VIEWPORT_MARGIN),
            window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
        );

        setStyle({
            position: 'fixed',
            left,
            width: MENU_WIDTH,
            ...(abrirParaCima
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
        });
    };

    useLayoutEffect(() => {
        if (open) posicionar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handleOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        const handleScrollOrResize = () => setOpen(false);

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [open]);

    return (
        <>
            <Button
                innerRef={triggerRef}
                title={title ?? 'Ações'}
                onClick={() => setOpen(o => !o)}
            >
                <i className="fa-solid fa-ellipsis-vertical"></i>
            </Button>
            {open && createPortal(
                <div
                    ref={menuRef}
                    className="actions-menu-popover"
                    style={style}
                >
                    {items.map((item, i) => (
                        <React.Fragment key={i}>
                            {item.dividerBefore && <div className="actions-menu-divider" />}
                            <button
                                type="button"
                                className={`actions-menu-item${item.danger ? ' actions-menu-item-danger' : ''}`}
                                disabled={item.disabled}
                                onClick={() => {
                                    setOpen(false);
                                    item.onClick();
                                }}
                            >
                                {item.icon && <i className={`${item.icon} actions-menu-item-icon`}></i>}
                                {item.label}
                            </button>
                        </React.Fragment>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

export default ActionsMenu;
